import { useCallback, useEffect, useRef } from 'react';

import { usePico } from '../pico';

const MAX_PENDING_LIVE_STREAM_DESTINATION_ACTIONS: number = 32;

/** Left sidebar panel ids (Notion: live stream broadcaster). */
export type LiveStreamBroadcasterPanel =
  | 'speakers'
  | 'templates'
  | 'media'
  | 'graphics'
  | 'brand'
  | 'q_and_a'
  | 'polls';

export type LiveStreamBroadcasterDisplayedProps = {
  liveEventId: string | null;
};

export type LiveStreamSidebarPanelClickedProps = {
  liveStreamPanel: LiveStreamBroadcasterPanel;
};

export type LiveStreamSpeakerInvitedProps = {
  liveStreamInviteMethod: 'generate_invite_link' | 'send_invite_via_email';
};

export type LiveStreamBackstageToggledProps = {
  liveStreamNewStatus: boolean;
};

export type LiveStreamTemplateSelectedProps = {
  liveStreamTemplateType: string;
};

export type LiveStreamMediaAddedProps = {
  liveStreamMediaType: 'videos' | 'images' | 'slides';
};

export type LiveStreamLowerThirdSavedProps = {
  liveStreamHasImage: boolean;
};

export type LiveStreamBrandLogoAddedProps = {
  liveStreamLogoSource: 'upload' | 'brand_kit';
};

export type LiveStreamBrandColorChangedProps = {
  liveStreamColorType: 'primary' | 'secondary';
};

export type LiveStreamSettingsSectionClickedProps = {
  liveStreamSettingsSection:
    | 'basics'
    | 'privacy'
    | 'schedule'
    | 'production'
    | 'closed_captions'
    | 'registration'
    | 'destinations';
};

export type LiveStreamBasicsChangedProps = {
  liveStreamBasicsField: 'title' | 'description';
};

export type LiveStreamPrivacyChangedProps = {
  liveStreamPrivacyType:
    | 'link_privacy'
    | 'embed_privacy'
    | 'content_rating'
    | 'contains_advertisement';
  liveStreamPrivacyValue: string;
};

export type LiveStreamScheduleConfiguredProps = {
  liveStreamHasStartDate: boolean;
  liveStreamHasEndDate: boolean;
  liveStreamRecurringEvent: boolean;
};

export type LiveStreamProductionModeChangedProps = {
  liveStreamProductionMode:
    | 'stream_from_browser'
    | 'prerecorded_event'
    | 'recording_only'
    | 'external_encoder';
};

export type LiveStreamLatencyChangedProps = {
  liveStreamLatency: 'low' | 'standard' | 'fail_safe';
};

export type LiveStreamClosedCaptionsToggledProps = {
  liveStreamNewStatus: boolean;
};

export type LiveStreamRegistrationToggledProps = {
  liveStreamNewStatus: boolean;
};

export type LiveStreamDestinationActionClickedProps = {
  liveStreamDestination:
    | 'vimeo'
    | 'facebook'
    | 'youtube'
    | 'linkedin'
    | 'vimeo_ott'
    | 'instagram'
    | 'tiktok'
    | 'x'
    | 'rtmp';
  liveStreamDestinationAction: 'connect' | 'add' | 'customize';
};

export type LiveStreamRegistrationSectionClickedProps = {
  liveStreamRegistrationSection: 'registrants' | 'form' | 'email';
};

export type LiveStreamRegistrationFormTabChangedProps = {
  liveStreamFormTab: 'fields' | 'appearance';
};

export type LiveStreamRegistrationFieldAddedProps = {
  liveStreamFieldCategory: 'registration_field' | 'hidden_field';
};

export type LiveStreamRegistrationAppearanceChangedProps = {
  liveStreamAppearanceSetting:
    | 'layout'
    | 'logo'
    | 'background'
    | 'overlay'
    | 'date_toggle'
    | 'add_to_calendar_toggle';
};

export type LiveStreamRegistrationBackgroundAddedProps = {
  liveStreamBackgroundSource: 'upload' | 'select_from_video';
};

export type LiveStreamRegistrationPreviewChangedProps = {
  liveStreamPreviewType: 'form' | 'confirmation' | 'sign_in_screen';
};

export type LiveStreamRegistrationEmailToggledProps = {
  liveStreamEmailType: 'confirmation' | 'reminder' | 'follow_up';
  liveStreamNewStatus: boolean;
};

export type LiveStreamRegistrantsActionClickedProps = {
  liveStreamRegistrantsAction: 'sync_from_email_list' | 'upload_csv' | 'export_csv' | 'share_event';
};

export type LiveStreamQaOverflowActionClickedProps = {
  liveStreamQaAction: 'stop_qa' | 'download' | 'copy_link' | 'open_in_new_tab';
};

export type LiveStreamPollCreatedProps = {
  liveStreamPollOptionCount: number;
};

export type LiveStreamChatToggledProps = {
  liveStreamNewStatus: boolean;
};

export type LiveStreamChatTabChangedProps = {
  liveStreamChatTab: 'audience' | 'speakers';
};

export type LiveStreamChatOverflowActionClickedProps = {
  liveStreamChatAction: 'download' | 'copy_link' | 'open_in_new_tab';
};

export type LiveStreamTopBarActionClickedProps = {
  liveStreamTopBarAction: 'analytics' | 'settings' | 'chat' | 'share';
};

export type LiveStreamEmbedConfiguredProps = {
  liveStreamEmbedType: 'player_and_engagement_tools' | 'player_only' | 'engagement_tools_only';
  liveStreamEmbedPrivacy: 'anywhere' | 'nowhere' | 'specific_domains';
};

export type LiveStreamToolbarActionClickedProps = {
  liveStreamToolbarAction: 'toggle_mic' | 'toggle_camera' | 'teleprompter' | 'share_screen';
};

export type LiveStreamCustomKeywordUseProps = {
  liveStreamCustomKeywordCount: number;
};

/**
 * PicoX events for `vimeo.com/live/broadcaster` (see Vimeo Core User Flow — All Events in Notion).
 */
export const useLiveStreamBroadcasterTracking = () => {
  const pico = usePico();
  const destinationActionPendingRef = useRef<Array<LiveStreamDestinationActionClickedProps>>([]);

  useEffect(() => {
    if (pico === null) {
      return;
    }
    for (const pending of destinationActionPendingRef.current) {
      pico.track('live_stream_destination_action_clicked', {
        live_stream_destination: pending.liveStreamDestination,
        live_stream_destination_action: pending.liveStreamDestinationAction,
      });
    }
    destinationActionPendingRef.current = [];
  }, [pico]);

  const trackLiveStreamBroadcasterDisplayed = useCallback(
    (props: LiveStreamBroadcasterDisplayedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('live_stream_broadcaster_displayed', {
        live_event_id: props.liveEventId ?? '',
      });
    },
    [pico],
  );

  const trackLiveStreamSidebarPanelClicked = useCallback(
    (props: LiveStreamSidebarPanelClickedProps) => {
      pico?.track('live_stream_sidebar_panel_clicked', {
        live_stream_panel: props.liveStreamPanel,
      });
    },
    [pico],
  );

  const trackLiveStreamSpeakerInvited = useCallback(
    (props: LiveStreamSpeakerInvitedProps) => {
      pico?.track('live_stream_speaker_invited', {
        live_stream_invite_method: props.liveStreamInviteMethod,
      });
    },
    [pico],
  );

  const trackLiveStreamBackstageToggled = useCallback(
    (props: LiveStreamBackstageToggledProps) => {
      pico?.track('live_stream_backstage_toggled', {
        live_stream_new_status: props.liveStreamNewStatus,
      });
    },
    [pico],
  );

  const trackLiveStreamTemplateSelected = useCallback(
    (props: LiveStreamTemplateSelectedProps) => {
      pico?.track('live_stream_template_selected', {
        live_stream_template_type: props.liveStreamTemplateType,
      });
    },
    [pico],
  );

  const trackLiveStreamSceneAdded = useCallback(() => {
    pico?.track('live_stream_scene_added', {});
  }, [pico]);

  const trackLiveStreamLayoutSelected = useCallback(() => {
    pico?.track('live_stream_layout_selected', {});
  }, [pico]);

  const trackLiveStreamMediaAdded = useCallback(
    (props: LiveStreamMediaAddedProps) => {
      pico?.track('live_stream_media_added', {
        live_stream_media_type: props.liveStreamMediaType,
      });
    },
    [pico],
  );

  const trackLiveStreamLowerThirdSaved = useCallback(
    (props: LiveStreamLowerThirdSavedProps) => {
      pico?.track('live_stream_lower_third_saved', {
        live_stream_has_image: props.liveStreamHasImage,
      });
    },
    [pico],
  );

  const trackLiveStreamBrandLogoAdded = useCallback(
    (props: LiveStreamBrandLogoAddedProps) => {
      pico?.track('live_stream_brand_logo_added', {
        live_stream_logo_source: props.liveStreamLogoSource,
      });
    },
    [pico],
  );

  const trackLiveStreamBrandColorChanged = useCallback(
    (props: LiveStreamBrandColorChangedProps) => {
      pico?.track('live_stream_brand_color_changed', {
        live_stream_color_type: props.liveStreamColorType,
      });
    },
    [pico],
  );

  const trackLiveStreamSettingsSectionClicked = useCallback(
    (props: LiveStreamSettingsSectionClickedProps) => {
      pico?.track('live_stream_settings_section_clicked', {
        live_stream_settings_section: props.liveStreamSettingsSection,
      });
    },
    [pico],
  );

  const trackLiveStreamBasicsChanged = useCallback(
    (props: LiveStreamBasicsChangedProps) => {
      pico?.track('live_stream_basics_changed', {
        live_stream_basics_field: props.liveStreamBasicsField,
      });
    },
    [pico],
  );

  const trackLiveStreamPrivacyChanged = useCallback(
    (props: LiveStreamPrivacyChangedProps) => {
      pico?.track('live_stream_privacy_changed', {
        live_stream_privacy_type: props.liveStreamPrivacyType,
        live_stream_privacy_value: props.liveStreamPrivacyValue,
      });
    },
    [pico],
  );

  const trackLiveStreamScheduleConfigured = useCallback(
    (props: LiveStreamScheduleConfiguredProps) => {
      pico?.track('live_stream_schedule_configured', {
        live_stream_has_start_date: props.liveStreamHasStartDate,
        live_stream_has_end_date: props.liveStreamHasEndDate,
        live_stream_recurring_event: props.liveStreamRecurringEvent,
      });
    },
    [pico],
  );

  const trackLiveStreamProductionModeChanged = useCallback(
    (props: LiveStreamProductionModeChangedProps) => {
      pico?.track('live_stream_production_mode_changed', {
        live_stream_production_mode: props.liveStreamProductionMode,
      });
    },
    [pico],
  );

  const trackLiveStreamLatencyChanged = useCallback(
    (props: LiveStreamLatencyChangedProps) => {
      pico?.track('live_stream_latency_changed', {
        live_stream_latency: props.liveStreamLatency,
      });
    },
    [pico],
  );

  const trackLiveStreamClosedCaptionsToggled = useCallback(
    (props: LiveStreamClosedCaptionsToggledProps) => {
      pico?.track('live_stream_closed_captions_toggled', {
        live_stream_new_status: props.liveStreamNewStatus,
      });
    },
    [pico],
  );

  const trackLiveStreamCustomKeywordUse = useCallback(
    (props: LiveStreamCustomKeywordUseProps) => {
      pico?.track('live_stream_custom_keyword_use', {
        live_stream_custom_keyword_count: props.liveStreamCustomKeywordCount,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationToggled = useCallback(
    (props: LiveStreamRegistrationToggledProps) => {
      pico?.track('live_stream_registration_toggled', {
        live_stream_new_status: props.liveStreamNewStatus,
      });
    },
    [pico],
  );

  const trackLiveStreamDestinationActionClicked = useCallback(
    (props: LiveStreamDestinationActionClickedProps) => {
      if (pico === null) {
        if (
          destinationActionPendingRef.current.length < MAX_PENDING_LIVE_STREAM_DESTINATION_ACTIONS
        ) {
          destinationActionPendingRef.current.push(props);
        }
        return;
      }
      pico.track('live_stream_destination_action_clicked', {
        live_stream_destination: props.liveStreamDestination,
        live_stream_destination_action: props.liveStreamDestinationAction,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationSectionClicked = useCallback(
    (props: LiveStreamRegistrationSectionClickedProps) => {
      pico?.track('live_stream_registration_section_clicked', {
        live_stream_registration_section: props.liveStreamRegistrationSection,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationFormTabChanged = useCallback(
    (props: LiveStreamRegistrationFormTabChangedProps) => {
      pico?.track('live_stream_registration_form_tab_changed', {
        live_stream_form_tab: props.liveStreamFormTab,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationFieldAdded = useCallback(
    (props: LiveStreamRegistrationFieldAddedProps) => {
      pico?.track('live_stream_registration_field_added', {
        live_stream_field_category: props.liveStreamFieldCategory,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationPresetChanged = useCallback(() => {
    pico?.track('live_stream_registration_preset_changed', {});
  }, [pico]);

  const trackLiveStreamRegistrationAppearanceChanged = useCallback(
    (props: LiveStreamRegistrationAppearanceChangedProps) => {
      pico?.track('live_stream_registration_appearance_changed', {
        live_stream_appearance_setting: props.liveStreamAppearanceSetting,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationBackgroundAdded = useCallback(
    (props: LiveStreamRegistrationBackgroundAddedProps) => {
      pico?.track('live_stream_registration_background_added', {
        live_stream_background_source: props.liveStreamBackgroundSource,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationPreviewChanged = useCallback(
    (props: LiveStreamRegistrationPreviewChangedProps) => {
      pico?.track('live_stream_registration_preview_changed', {
        live_stream_preview_type: props.liveStreamPreviewType,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrationEmailToggled = useCallback(
    (props: LiveStreamRegistrationEmailToggledProps) => {
      pico?.track('live_stream_registration_email_toggled', {
        live_stream_email_type: props.liveStreamEmailType,
        live_stream_new_status: props.liveStreamNewStatus,
      });
    },
    [pico],
  );

  const trackLiveStreamRegistrantsActionClicked = useCallback(
    (props: LiveStreamRegistrantsActionClickedProps) => {
      pico?.track('live_stream_registrants_action_clicked', {
        live_stream_registrants_action: props.liveStreamRegistrantsAction,
      });
    },
    [pico],
  );

  const trackLiveStreamQaStarted = useCallback(() => {
    pico?.track('live_stream_qa_started', {});
  }, [pico]);

  const trackLiveStreamQaEnded = useCallback(() => {
    pico?.track('live_stream_qa_ended', {});
  }, [pico]);

  const trackLiveStreamQaOverflowActionClicked = useCallback(
    (props: LiveStreamQaOverflowActionClickedProps) => {
      pico?.track('live_stream_qa_overflow_action_clicked', {
        live_stream_qa_action: props.liveStreamQaAction,
      });
    },
    [pico],
  );

  const trackLiveStreamPollCreated = useCallback(
    (props: LiveStreamPollCreatedProps) => {
      pico?.track('live_stream_poll_created', {
        live_stream_poll_option_count: props.liveStreamPollOptionCount,
      });
    },
    [pico],
  );

  const trackLiveStreamChatToggled = useCallback(
    (props: LiveStreamChatToggledProps) => {
      pico?.track('live_stream_chat_toggled', {
        live_stream_new_status: props.liveStreamNewStatus,
      });
    },
    [pico],
  );

  const trackLiveStreamChatTabChanged = useCallback(
    (props: LiveStreamChatTabChangedProps) => {
      pico?.track('live_stream_chat_tab_changed', {
        live_stream_chat_tab: props.liveStreamChatTab,
      });
    },
    [pico],
  );

  const trackLiveStreamChatOverflowActionClicked = useCallback(
    (props: LiveStreamChatOverflowActionClickedProps) => {
      pico?.track('live_stream_chat_overflow_action_clicked', {
        live_stream_chat_action: props.liveStreamChatAction,
      });
    },
    [pico],
  );

  const trackLiveStreamTopBarActionClicked = useCallback(
    (props: LiveStreamTopBarActionClickedProps) => {
      pico?.track('live_stream_top_bar_action_clicked', {
        live_stream_top_bar_action: props.liveStreamTopBarAction,
      });
    },
    [pico],
  );

  const trackLiveStreamShareLinkCopied = useCallback(() => {
    pico?.track('live_stream_share_link_copied', {});
  }, [pico]);

  const trackLiveStreamEmbedConfigured = useCallback(
    (props: LiveStreamEmbedConfiguredProps) => {
      pico?.track('live_stream_embed_configured', {
        live_stream_embed_type: props.liveStreamEmbedType,
        live_stream_embed_privacy: props.liveStreamEmbedPrivacy,
      });
    },
    [pico],
  );

  const trackLiveStreamToolbarActionClicked = useCallback(
    (props: LiveStreamToolbarActionClickedProps) => {
      pico?.track('live_stream_toolbar_action_clicked', {
        live_stream_toolbar_action: props.liveStreamToolbarAction,
      });
    },
    [pico],
  );

  const trackLiveStreamGoLiveClicked = useCallback(() => {
    pico?.track('live_stream_go_live_clicked', {});
  }, [pico]);

  return {
    trackLiveStreamBroadcasterDisplayed,
    trackLiveStreamSidebarPanelClicked,
    trackLiveStreamSpeakerInvited,
    trackLiveStreamBackstageToggled,
    trackLiveStreamTemplateSelected,
    trackLiveStreamSceneAdded,
    trackLiveStreamLayoutSelected,
    trackLiveStreamMediaAdded,
    trackLiveStreamLowerThirdSaved,
    trackLiveStreamBrandLogoAdded,
    trackLiveStreamBrandColorChanged,
    trackLiveStreamSettingsSectionClicked,
    trackLiveStreamBasicsChanged,
    trackLiveStreamPrivacyChanged,
    trackLiveStreamScheduleConfigured,
    trackLiveStreamProductionModeChanged,
    trackLiveStreamLatencyChanged,
    trackLiveStreamClosedCaptionsToggled,
    trackLiveStreamCustomKeywordUse,
    trackLiveStreamRegistrationToggled,
    trackLiveStreamDestinationActionClicked,
    trackLiveStreamRegistrationSectionClicked,
    trackLiveStreamRegistrationFormTabChanged,
    trackLiveStreamRegistrationFieldAdded,
    trackLiveStreamRegistrationPresetChanged,
    trackLiveStreamRegistrationAppearanceChanged,
    trackLiveStreamRegistrationBackgroundAdded,
    trackLiveStreamRegistrationPreviewChanged,
    trackLiveStreamRegistrationEmailToggled,
    trackLiveStreamRegistrantsActionClicked,
    trackLiveStreamQaStarted,
    trackLiveStreamQaEnded,
    trackLiveStreamQaOverflowActionClicked,
    trackLiveStreamPollCreated,
    trackLiveStreamChatToggled,
    trackLiveStreamChatTabChanged,
    trackLiveStreamChatOverflowActionClicked,
    trackLiveStreamTopBarActionClicked,
    trackLiveStreamShareLinkCopied,
    trackLiveStreamEmbedConfigured,
    trackLiveStreamToolbarActionClicked,
    trackLiveStreamGoLiveClicked,
  };
};
